import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { DATASET_GROUP_TYPE_MERGED } from 'webapp/const/template-resolve';
import { OrderDirection } from 'webapp/enums/enums';
import type { SearchProps } from 'webapp/utils/filter';

import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type Report from 'webapp/models-custom/report';

export default class FilterSortReportAccountTemplate extends FilterSort<Report> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	@tracked override sortProp = 'order';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.ASC;

	override settingsNamePrefix = 'reportAccountTemplateFilterSettings';
	override trackingObjectName = 'reportAccountTemplate';

	override searchProps: SearchProps = {
		'templateRecord.title': 1,
		'templateRecord.description': 1,
	};

	override itemDomSelector = 'li.filter-sort-item';

	constructor(options: FilterSortSettings<Report>) {
		super(options);

		const selectedFilters = this.readSavedSelectedFilters();

		// Data source
		this.createFilter<string>({
			id: 2,
			label: 'Data source',
			name: 'data source',
			selected: selectedFilters[2] as string | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.datasourceOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((sourceTypeId) => {
					if (sourceTypeId === DATASET_GROUP_TYPE_MERGED) {
						return item.templateModel?.hasMergedDatasetMetrics === true;
					}
					return (item.templateModel?.specificSourceTypesKeys || []).includes(
						sourceTypeId,
					);
				});
			},
		});

		// Created by
		this.createFilter<number>({
			id: 3,
			label: 'Created by',
			name: 'creator',
			selected: selectedFilters[3] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.creatorOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((selectedValue) => {
					if (selectedValue === -1) {
						// exception: all others
						return !item.isMine;
					} else {
						return item.createdBy === selectedValue;
					}
				});
			},
		});

		// Tags (used only)
		this.createFilter<number>({
			id: 1,
			label: 'Tags',
			name: 'tag',
			selected: selectedFilters[1] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.tagOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((selectedValue) => item.tags.includes(selectedValue));
			},
		});
	}

	get tagOptions(): FilterOption<number>[] {
		const arrayCopy = Array.from(
			this.mmReportsService.assignedTagsForAccountTemplates,
		) as FilterOption<number>[];
		if (arrayCopy.length) {
			arrayCopy.unshift({ id: 0, text: 'All' });
		}
		return arrayCopy;
	}

	@cached
	get datasourceOptions(): FilterOption<string>[] {
		const sourceTypeArray: string[] = [];
		let hasMerged = false;

		this.allItems.forEach((board) => {
			(board.templateModel?.specificSourceTypesKeys || []).forEach((id: string) => {
				sourceTypeArray.push(id);
			});
			if (board.templateModel?.hasMergedDatasetMetrics) {
				hasMerged = true;
			}
		});

		const list = this.mmSourceTypesService.getSourceTypesListWithCount(sourceTypeArray);

		if (hasMerged) {
			list.push({
				id: DATASET_GROUP_TYPE_MERGED,
				text: 'Merged dataset',
				img: 'https://cdn1.databox.com/images/apps/s/Merged.png',
			});
		}

		list.unshift({ id: 'all', text: 'All' });

		return list;
	}

	@cached
	get creatorOptions(): FilterOption<number>[] {
		const allItemCountByCreator: Record<number, number> = {};
		this.allItems.forEach((board: Report) => {
			const createdById = board.createdBy;
			if (!allItemCountByCreator[createdById]) {
				allItemCountByCreator[createdById] = 0;
			}
			allItemCountByCreator[createdById]++;
		});

		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByCreator);
	}
}
